import React, { useEffect, useState, useRef, useContext } from "react";
import { TableHeader } from "../../../components/table/TableHeader";
import { AiOutlineSearch, AiOutlineCloseCircle } from "react-icons/ai";
import Head from "next/head";
import TableCellAdjSQ from "../../../components/table/TableCellAdjSQ";
import TableCellRank from "../../../components/table/TableCellRank";
import { Pagination } from "../../../components/pagination/Pagination";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import Link from "next/link";
import { useRouter } from "next/router";
import { AppContext } from "../../../context/state";
import Utils from "../../../utils/Utils";
import { authGetRequest } from "../../../utils/authGetRequest";
import Input from "../../../components/form/Input";
import MainLayout from "../../../components/MainLayout/MainLayout";
import { useCookies } from "react-cookie";
import logoNBA from "../../../assets/images/League_NBA.png";
import logoNCAA from "../../../assets/images/League_NCAA.png";
import WNBALogo from "../../../assets/images/WNBALogo.png";
import Image from "next/image";
import WinningMetrics from "../../../components/WinningMetrics";
import { getDefYear } from "../../../constants/main";

let ascOrder = true;

const teamStatsHeader = [
  {
    title: "Team",
    showImage: false,
    sortable: true,
    key: "Team_Name",
  },
  {
    title: "Conference",
    showImage: false,
    sortable: true,
    key: "Conference",
  },
  {
    title: "adj SQ",
    showImage: false,
    sortable: true,
    key: "Adjusted_Shot_Quality",
    tooltip: {
      title: "Adjusted Shot Quality",
      text: "Adjusted ShotQuality difference: adjOFF SQ minus adjDEF SQ.",
      link: "/stats-explained#adj-sq",
    },
  },
  {
    title: "adjOFF SQ",
    showImage: false,
    sortable: true,
    key: "Offensive_Shot_Quality",
    rankKey: "Offensive_ShotQuality_Rank",
    tooltip: {
      title: "Adjusted Offensive ShotQuality",
      text: "Offensive SQ points divided by possessions, but adjusting for strength of schedule for each team",
      link: "/stats-explained#adj-sq",
    },
  },
  {
    title: "adjDEF SQ",
    showImage: false,
    sortable: true,
    key: "Defensive_Shot_Quality",
    rankKey: "Defensive_ShotQuality_Rank",
    tooltip: {
      title: "Adjusted Defensive ShotQuality",
      text: "Defensive SQ points divided by possessions, but adjusting for strength of schedule for each team.",
      link: "/stats-explained#adj-sq",
    },
  },
  {
    title: "Rim & 3 Rate",
    showImage: false,
    sortable: true,
    key: "rim_and_3_rate",
    tooltip: {
      title: "Rim & 3 Rate",
      text: "Percent of possession that resulted in a three pointer or a shot at the rim (the two most valued shots!)",
      link: "/stats-explained#Rim&3",
    },
  },
  {
    title: "Record",
    showImage: false,
    sortable: true,
    key: "Actual_Record",
  },
  {
    title: "SQ Record",
    showImage: false,
    sortable: true,
    key: "SQ_Record",
    classList: ["min-width-100px"],
    tooltip: {
      title: "Shot Quality Record",
      text: "Projected record based on shots taken by both teams. Total accumulation of “win game %” stat.",
      link: "/stats-explained#Rim&3",
    },
  },
  {
    title: "Win%",
    showImage: false,
    sortable: true,
    key: "Actual_Win_Percentage",
  },
  {
    title: "SQ Win%",
    showImage: false,
    sortable: true,
    key: "SQ_Win_Percentage",
    tooltip: {
      title: "Shot Quality Win Percentage",
      text: "Projected win percentage based on Shot Quality",
    },
  },
  {
    title: "Record Luck",
    showImage: false,
    sortable: true,
    key: "Record_Luck",
    tooltip: {
      title: "Record Luck",
      text: "(SQ Win Percentage - Actual Win Percentage) *100. If positive, team is due for positive regression. If negative, team is due for negative regression!",
      link: "/stats-explained#record-luck",
    },
  },
];

const getDynamicRanks = (arr = []) => {
  const getTeamRank = (teamName, sortedArr) => {
    return sortedArr.findIndex((elem) => elem["Team_Name"] === teamName) + 1;
  };

  let sortedOffensive = [...arr.sort(Utils.dynamicsort("Offensive_Shot_Quality", "desc"))];
  let sortedDefensive = [...arr.sort(Utils.dynamicsort("Defensive_Shot_Quality", "asc"))];
  let sortedRimAndRate3 = [...arr.sort(Utils.dynamicsort("rim_and_3_rate", "desc"))];

  let sortedAndRankedResponse = arr.sort(Utils.dynamicsort("Adjusted_Shot_Quality", "desc")).map((elem, index) => {
    return {
      ...elem,

      adjSQRank: index + 1,
      offensiveRank: getTeamRank(elem["Team_Name"], sortedOffensive),
      defensiveRank: getTeamRank(elem["Team_Name"], sortedDefensive),
      rimAndRate3Rank: getTeamRank(elem["Team_Name"], sortedRimAndRate3),
    };
  });

  return sortedAndRankedResponse;
};

const TeamStandings = ({ conference }) => {
  const router = useRouter();
  let leagueFromUrl = router.query?.league;
  if (leagueFromUrl === "ncaa") {
    leagueFromUrl = "college_mens";
  } else if (leagueFromUrl === "ncaaw") {
    leagueFromUrl = "college_womens";
  }
  const newLeague =
    leagueFromUrl === "college_mens" ? "ncaa" : leagueFromUrl === "college_womens" ? "ncaaw" : leagueFromUrl;
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [user, setUser] = useState(null);
  const [domLoaded, setDomLoaded] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const isPremiumPlan = Utils.hasPremiumPlan(user);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("user")));
    setAuthToken(document.cookie.split("token_access=").pop().split(";").shift());
    document.title = "ShotQuality | Team Standings";
    setDomLoaded(true);
    ascOrder = true;
  }, []);

  const [cookies, setCookie] = useCookies(["token_access, name"]);

  const { store, setStore } = useContext(AppContext);

  let headers = teamStatsHeader;
  if (leagueFromUrl === "wnba") {
    headers = teamStatsHeader.filter((item) => item.key !== "Conference");
  }
  const [teams, setTeams] = useState([]);
  const [rawTeams, setRawTeams] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [coachData, setCoachData] = useState({
    confName: "",
    teams: [],
    isDisplayed: false,
  });
  const [sortedKey, setSortedKey] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [year, setYear] = useState(getDefYear(leagueFromUrl));
  const inputSearchRef = useRef(null);
  const [searchAutocomplete, setSearchAutocomplete] = useState({
    team: [],
    conference: [],
  });
  const [activeDate, setActiveDate] = useState(new Date());
  const [displaySearchResults, setDisplaySearchResults] = useState(false);
  const [gameUpdates, setGameUpdates] = useState([]);

  const [slicedArray, setSlicedArray] = useState([]);
  const [slicedArrayIndex, setSlicedArrayIndex] = useState(0);

  const handleLoadMore = () => {
    setSlicedArrayIndex(slicedArrayIndex + 40);
  };

  useEffect(() => {
    setSlicedArray(filtered.slice(0, slicedArrayIndex + 40));
  }, [filtered, slicedArrayIndex]);

  const showLoadMoreButton = slicedArray.length < filtered.length;

  const planDate = new Date().toLocaleDateString("en-GB").replaceAll("/", "");

  useEffect(() => {
    if (leagueFromUrl) {
      setStore({ ...store, leagueType: leagueFromUrl });
    }
    // async function load() {
    if (authToken) {
      authGetRequest(`/api/${leagueFromUrl}/${year}/team-standings`, authToken)
        .then((response) => {
          if (response.status !== 200) {
            let tempData = {
              data: [
                {
                  Actual_Record: "N/A",
                  Actual_Win_Percentage: 0.0,
                  Conference: NaN,
                  Defensive_Shot_Quality: 0.0,
                  Defensive_Shot_Quality_Rank_Visual: NaN,
                  Offensive_Shot_Quality: 0,
                  Offensive_Shot_Quality_Rank_Visual: NaN,
                  Record_Luck: 0.0,
                  SQ_Record: "N/A",
                  NET_SQ: 0,
                  SQ_Win_Percentage: 0.0,
                  Team_Name: NaN,
                  Adjusted_Shot_Quality: 0.0,
                },
              ],
            };
            return tempData;
          }
          return response.data;
        })
        .then((response) => {
          sessionStorage.setItem(`teamStandingsCount${year}`, response.data.length);

          return response.data.map((dt) => {
            return {
              ...dt,
              Adjusted_Shot_Quality: Number.parseFloat(dt["NET_SQ"]?.toFixed(2)),
            };
          });
        })
        .then((response) => {
          let sortedAndRankedResponse = getDynamicRanks(response);
          const sessionObj = {};
          sessionObj[year] = sortedAndRankedResponse;
          sessionStorage.setItem("TeamStandings", JSON.stringify(sessionObj));
          setSortedKey("Adjusted_Shot_Quality");
          setTeams(sortedAndRankedResponse);
          setRawTeams(sortedAndRankedResponse);
        })
        .catch(async (err) => {
          if (err.message.includes("401")) {
            try {
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/refresh`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                body: authToken,
              })
                .then((res) => res.json())
                .then((response) => {
                  setCookie("token_access", response.access_token);
                  setAuthToken(response.access_token);
                });
            } catch (error) {
              console.log(error);
            }
          }
          console.log("Request failed :(");
        })
        .finally(() => {
          setIsDataLoaded(true);
        });
      authGetRequest(`/api/${leagueFromUrl}/${year}/${planDate}/team_schedule`, authToken)
        .then((response) => {
          if (response.status === 401) {
            router.push("/login");
            window.location.reload();
          }
          return response.data;
        })
        .then((res) => {
          if (res.data) {
            let uniqueArray = res.data.filter((team) => {
              return team.Location === "Away";
            });
            setGameUpdates(uniqueArray);
            setActiveDate(new Date(res.data[0]["Date"].split("/").reverse().join("/")));
          } else {
            setGameUpdates([]);
          }
        })
        .catch(async (err) => {
          if (err.message.includes("401")) {
            try {
              const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL_API_URL}/api/refresh`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
                body: authToken,
              })
                .then((res) => res.json())
                .then((response) => {
                  setCookie("token_access", response.access_token);
                  setAuthToken(response.access_token);
                });
            } catch (error) {
              console.log(error);
            }
          }
          console.log("something went wrong at team_schedule");
        })
        .finally(() => {
          setIsDataLoaded(true);
        });
    }
  }, [year, leagueFromUrl, authToken]);

  const sort = (key) => {
    let res = [];
    let rawData = searchKey.length > 0 ? filtered : teams;

    let replaceKey;

    if (key === "Actual_Record") {
      replaceKey = "Actual_Win_Percentage";
    } else if (key === "SQ_Record") {
      replaceKey = "SQ_Win_Percentage";
    } else {
      replaceKey = key;
    }

    if (sortedKey === key) {
      let order = ascOrder ? "asc" : "desc";

      res = rawData.sort(Utils.dynamicsort(replaceKey, order));
      ascOrder = order === "desc";
    } else {
      ascOrder = false;
      setSortedKey(key);
      res = rawData.sort(Utils.dynamicsort(replaceKey));
    }
    setTeams([...res]);
  };

  const searchByTeam = (searchTerm) => {
    return Array.from(
      new Set(
        rawTeams
          .filter((team) => team["Team_Name"]?.toLowerCase().includes(searchTerm.toLowerCase()))
          .map((team) => team["Team_Name"])
      )
    );
  };
  const searchByConference = (searchTerm) => {
    return Array.from(
      new Set(
        rawTeams
          .filter((team) => team["Conference"]?.toLowerCase().includes(searchTerm.toLowerCase()))
          .map((team) => team["Conference"])
      )
    );
  };

  const searchData = (searchTerm, searchType) => {
    let newRecords;
    if (searchType === "team") {
      newRecords = rawTeams.filter((r) => r["Team_Name"] === searchTerm);
    } else {
      newRecords = rawTeams.filter((r) => r["Conference"] === searchTerm);
    }

    return newRecords;
  };

  const displaySearchData = (e, searchType) => {
    inputSearchRef.current.value = e.target.innerText;
    setSearchKey(e.target.innerText);
    setTeams([...searchData(e.target.innerText, searchType)]);
    setDisplaySearchResults(false);
    if (searchType === "conference") {
      router.push(`/${newLeague}/team-standings/${e.target.innerText}`);
    }
  };

  const onPageChanged = (data) => {
    setFiltered([...data]);
  };

  const clearSearch = (e) => {
    setSearchKey("");
    setTeams(rawTeams);
    inputSearchRef.current.value = "";
    setDisplaySearchResults(false);
    router.push(`/${newLeague}/team-standings`);
  };

  useEffect(() => {
    setSearchAutocomplete({
      team: searchByTeam(searchKey).splice(0, 5),
      conference: searchByConference(searchKey).splice(0, 5),
    });
  }, [searchKey, setSearchKey]);

  const onYearClick = (yr) => {
    if (yr !== year) {
      setFiltered([]);
      setYear(yr);
      setCoachData({ confName: "", teams: "" });
    }
  };

  const handleInputChange = (e) => {
    setSearchKey(e.target.value);
    if (e.target.value.length > 0) {
      setDisplaySearchResults(true);
    }
  };

  useEffect(() => {
    setCoachData({
      confName: "",
      teams: [],
      isDisplayed: false,
    });
    if (store.isSignUpCoach && leagueFromUrl && rawTeams.length > 0) {
      const schoolName = localStorage.getItem("school") ?? "";
      const confName =
        rawTeams && rawTeams.length
          ? rawTeams.filter((team) => team["Team_Name"]?.toLowerCase() === schoolName?.toLowerCase())?.[0]?.[
              "Conference"
            ]
          : null;

      if (confName) {
        const confOnlyTeams = rawTeams.filter((team) => team["Conference"] === confName);

        if (confOnlyTeams.length > 0) {
          setCoachData({
            confName: confName,
            teams: getDynamicRanks(confOnlyTeams),
          });
        }
      }
    }
  }, [store, year, rawTeams]);

  const toggleCoachConference = () => {
    if (coachData["isDisplayed"]) {
      setCoachData((coachData) => {
        return {
          ...coachData,
          isDisplayed: false,
        };
      });

      setFiltered(rawTeams);
    } else {
      if (coachData["teams"].length > 0) {
        setCoachData((coachData) => {
          return {
            ...coachData,
            isDisplayed: true,
          };
        });

        setFiltered(coachData["teams"]);
      }
    }
  };

  const CoachConfButton = () => {
    return (
      store.isSignUpCoach &&
      coachData["teams"].length > 0 &&
      coachData["confName"].length > 0 &&
      !coachData["isDisplayed"] && (
        <button className="btn btn-lg btn-primary text-truncate" onClick={toggleCoachConference}>
          Show {coachData["confName"]} Conferences
        </button>
      )
    );
  };
  useEffect(() => {
    if (conference) {
      setTeams(rawTeams.filter((team) => team["Conference"].toLowerCase() === conference.toLowerCase()));
      setSearchKey(conference);
    }
  }, [conference, rawTeams]);

  const leagueNameCaps =
    newLeague === "ncaa" ? "NCAA Men's" : newLeague === "ncaaw" ? "NCAA Women's" : newLeague.toUpperCase();
  return (
    <>
      <Head>
        {conference ? (
          <title>
            {`${
              newLeague === "ncaaw"
                ? "NCAA Women's College"
                : newLeague === "ncaa"
                ? "NCAA Men's College"
                : newLeague.toUpperCase()
            } ${conference} Basketball Conference Standings`}
          </title>
        ) : (
          <title>{`${year} ${leagueNameCaps} Basketball Teams: Odds, Predictions Spreads & More | ShotQuality`}</title>
        )}

        <meta
          name="description"
          content={`Explore every ${leagueNameCaps} basketball team to easily find odds, predictions, spreads, picks and parlays, and more. ShotQuality
          Bets provides the most accurate, robust and predictable dataset for ${leagueNameCaps} basketball odds, picks and parlays. Access our dataset now to
          unlock all NCAAB odds and easily identify points-of-value for each game.`}
        />
      </Head>
      <MainLayout>
        <main id="ContentBox" style={{ minHeight: "100vh" }}>
          <div className="MiscStats pt-4">
            {/* Start Title side */}
            <div className=" text-center">
              <div className="container-fluid">
                <div className="dukescroe">
                  <div className="row flex flex-column align-items-center header-container">
                    <div className="box col-md-8 text-md-left">
                      <h2 className="teamStandingsTitle d-flex justify-content-center align-items-center">
                        {" "}
                        <Image
                          style={
                            leagueFromUrl === "wnba"
                              ? { marginRight: "0px", marginLeft: "24px", marginTop: "5px" }
                              : { marginTop: "2px" }
                          }
                          src={
                            leagueFromUrl === "nba"
                              ? logoNBA.src
                              : leagueFromUrl === "wnba"
                              ? WNBALogo.src
                              : logoNCAA.src
                          }
                          width="80"
                          height="80"
                          alt="selected League"
                        />{" "}
                        <span>
                          {" "}
                          {year} {conference ? conference.toUpperCase() + " " : null}
                          {newLeague.toUpperCase()} Team Standings and Stats
                        </span>
                      </h2>
                    </div>
                    {domLoaded && window.innerWidth > 375 && (
                      <div className="box col-md-4 text-md-right position-relative">
                        {filtered.length > 0 ? (
                          <div className="search position-relative w-100">
                            <Input
                              value={searchKey}
                              icon={<AiOutlineSearch />}
                              type="text"
                              placeholder="Search Team/Conference"
                              className="form-control w-100 search-input"
                              ref={inputSearchRef}
                              onInput={handleInputChange}
                              rightIcon={
                                inputSearchRef?.current?.value.length > 0 ? (
                                  <AiOutlineCloseCircle onClick={clearSearch} />
                                ) : null
                              }
                            />
                            {displaySearchResults === true ? (
                              <div className="search-results">
                                <h6 className="text-left -2">Teams</h6>
                                {searchAutocomplete["team"].length > 0 ? (
                                  searchAutocomplete["team"].map((searchTerm) => (
                                    <span
                                      key={searchTerm}
                                      className="search-results__text"
                                      onClick={(e) => displaySearchData(e, "team")}
                                    >
                                      {searchTerm}
                                    </span>
                                  ))
                                ) : (
                                  <span disabled="true" className="text-center">
                                    No Results Found
                                  </span>
                                )}
                                <h6 className="text-left -2">Conferences</h6>
                                {searchAutocomplete["conference"].length > 0 ? (
                                  searchAutocomplete["conference"].map((searchTerm) => (
                                    <span
                                      key={searchTerm}
                                      className="search-results__text"
                                      onClick={(e) => displaySearchData(e, "conference")}
                                    >
                                      {searchTerm}
                                    </span>
                                  ))
                                ) : (
                                  <span disabled="true" className="text-center">
                                    No Results Found
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <SkeletonTheme color="#a4abb3" highlightColor="#e3e6e8">
                            <Skeleton height="39px" width="100%" style={{ borderRadius: "30px" }} />
                          </SkeletonTheme>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* End Title side */}
            {/* Start Score side */}
            <div className="Scoreside text-center">
              <div className="container-fluid">
                <div className="dukescroe mb-3 mt-2">
                  <Pagination
                    records={teams}
                    onPageChanged={onPageChanged}
                    onYearClick={onYearClick}
                    page="TeamStandings"
                    allPages={true}
                    hideOnMobile={Utils.getDeviceType() === "Mobile" ? true : false}
                    store={store}
                    gameUpdates={gameUpdates}
                    year={year}
                    activeDate={activeDate}
                    filterByCoachConf={<CoachConfButton />}
                    showExportCsv={isPremiumPlan}
                    league={newLeague}
                  />
                  {teams.length > 0 ? <></> : <Skeleton height="50px" width="100%" />}

                  {domLoaded && window.innerWidth <= 375 && (
                    <div className="box col-md-4 mb-4 text-md-right position-relative">
                      {filtered.length > 0 ? (
                        <div className="search position-relative w-100">
                          <Input
                            icon={<AiOutlineSearch />}
                            type="text"
                            placeholder="Search Team/Conference"
                            className="form-control w-100 search-input"
                            ref={inputSearchRef}
                            onInput={handleInputChange}
                            rightIcon={
                              inputSearchRef?.current?.value.length > 0 ? (
                                <AiOutlineCloseCircle onClick={clearSearch} />
                              ) : null
                            }
                          />
                          {displaySearchResults === true ? (
                            <div className="search-results">
                              <h6 className="text-left -2">Teams</h6>
                              {searchAutocomplete["team"].length > 0 ? (
                                searchAutocomplete["team"].map((searchTerm) => (
                                  <span
                                    key={searchTerm}
                                    className="search-results__text"
                                    onClick={(e) => displaySearchData(e, "team")}
                                  >
                                    {searchTerm}
                                  </span>
                                ))
                              ) : (
                                <span disabled="true" className="text-center">
                                  No Results Found
                                </span>
                              )}
                              <h6 className="text-left -2">Conferences</h6>
                              {searchAutocomplete["conference"].length > 0 ? (
                                searchAutocomplete["conference"].map((searchTerm) => (
                                  <span
                                    key={searchTerm}
                                    className="search-results__text"
                                    onClick={(e) => displaySearchData(e, "conference")}
                                  >
                                    {searchTerm}
                                  </span>
                                ))
                              ) : (
                                <span disabled="true" className="text-center">
                                  No Results Found
                                </span>
                              )}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <SkeletonTheme color="#a4abb3" highlightColor="#e3e6e8">
                          <Skeleton height="39px" width="100%" style={{ borderRadius: "30px" }} />
                        </SkeletonTheme>
                      )}
                    </div>
                  )}
                  {filtered.length > 0 ? (
                    <div className="table-responsive table-space mt-3 responsive-to-card">
                      <table className="table tl-fixed-desktop position-relative ">
                        <thead style={{ zIndex: 9, background: "none" }}>
                          <tr>
                            {filtered.length > 0 ? (
                              <TableHeader
                                headers={headers}
                                onSortClick={sort}
                                ascOrder={ascOrder}
                                sortedKey={sortedKey}
                              />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {slicedArray.map((data, i) => {
                            const rankConfig = {
                              leagueType: coachData["isDisplayed"] ? "conference" : leagueFromUrl,
                              conferenceTeamCount: coachData["isDisplayed"] ? coachData["teams"].length : 0,
                            };
                            return (
                              <tr key={"teamsconf" + i}>
                                <td
                                  style={{
                                    //textAlign: "left",
                                    verticalAlign: "middle",
                                    fontFamily: "var(--font-inter)",
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    color: "#0F131B",
                                  }}
                                  data-name={teamStatsHeader[0].title}
                                  title={data[teamStatsHeader[0].key] ?? ""}
                                >
                                  {data[teamStatsHeader[0].key] ? (
                                    data[teamStatsHeader[5].key] === "0 - 0" ? (
                                      data[teamStatsHeader[0].key]
                                    ) : (
                                      <Link
                                        href={`/team/${newLeague}/${year}/${data[teamStatsHeader[0].key]}`}
                                        target={"_blank"}
                                      >
                                        {data[teamStatsHeader[0].key]}
                                      </Link>
                                    )
                                  ) : (
                                    "N/A"
                                  )}
                                </td>
                                {leagueFromUrl !== "wnba" && (
                                  <td title={data[teamStatsHeader[1].key]} data-name={teamStatsHeader[1].title}>
                                    <Link href={`/${newLeague}/team-standings/${data[teamStatsHeader[1].key]} `}>
                                      {data[teamStatsHeader[1].key] ? (
                                        data[teamStatsHeader[5].key] === "0 - 0" ? (
                                          data[teamStatsHeader[1].key]
                                        ) : (
                                          <p style={{ fontSize: "15px", marginBottom: 0 }}>
                                            {data[teamStatsHeader[1].key].length > 13
                                              ? data[teamStatsHeader[1].key].slice(0, 12).concat("...")
                                              : data[teamStatsHeader[1].key]}
                                          </p>
                                        )
                                      ) : (
                                        "N/A"
                                      )}
                                    </Link>
                                  </td>
                                )}

                                <TableCellAdjSQ
                                  data_name={teamStatsHeader[2].title}
                                  data={data[teamStatsHeader[2].key]}
                                  rank={data["adjSQRank"]}
                                  cellType="adjSQ"
                                  {...rankConfig}
                                />

                                <TableCellRank
                                  data_name={teamStatsHeader[3].title}
                                  rankValue={data["offensiveRank"]}
                                  {...rankConfig}
                                >
                                  {data[teamStatsHeader[3].key]}
                                </TableCellRank>
                                <TableCellRank
                                  data_name={teamStatsHeader[4].title}
                                  rankValue={data["defensiveRank"]}
                                  leagueType={leagueFromUrl}
                                >
                                  {data[teamStatsHeader[4].key]}
                                </TableCellRank>

                                <TableCellRank
                                  data_name={teamStatsHeader[5].title}
                                  rankValue={data["rimAndRate3Rank"]}
                                  {...rankConfig}
                                >
                                  {Utils.toPercent(data[teamStatsHeader[5].key])}
                                </TableCellRank>
                                <td data-name={teamStatsHeader[6].title}>{data[teamStatsHeader[6].key]}</td>
                                <td data-name={teamStatsHeader[7].title}>{data[teamStatsHeader[7].key]}</td>
                                <td data-name={teamStatsHeader[8].title}>
                                  {Utils.toPercent(data[teamStatsHeader[8].key])}
                                </td>
                                <td data-name={teamStatsHeader[9].title}>
                                  {Utils.toPercent(data[teamStatsHeader[9].key])}
                                </td>
                                {data[teamStatsHeader[10].key] > 0 ? (
                                  <td data-name={teamStatsHeader[10].title} style={{ color: "green" }}>
                                    +{data[teamStatsHeader[10].key]?.toFixed(2)}
                                  </td>
                                ) : (
                                  <td data-name={teamStatsHeader[10].title} style={{ color: "red" }}>
                                    {data[teamStatsHeader[10].key]?.toFixed(2)}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="text-center">
                    {showLoadMoreButton && (
                      <button className="btn btn-primary mt-3 mb-3" onClick={handleLoadMore}>
                        Load More..
                      </button>
                    )}
                  </div>
                  {/* {isDataLoaded && filtered?.length === 0 && leagueFromUrl === "nba" && (
                    <>
                      <div
                        style={{
                          display: "flex",

                          marginTop: "50px",
                        }}
                      >
                        <h3 style={{ margin: "0 auto" }}>
                          {" "}
                          🧠 The ShotQuality {leagueNameCaps} Model is Learning. {leagueNameCaps} Standings will be
                          available in 2-4 weeks
                        </h3>
                      </div>
                    </>
                  )} */}
                  {isDataLoaded && filtered?.length === 0 && leagueFromUrl && (
                    <div className="mt-5 mb-5" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                      <h3 style={{ margin: "0 auto" }}>There are no {leagueNameCaps} Team Standings available. </h3>
                    </div>
                  )}
                  <WinningMetrics targetPage={"TeamStandings"} />
                </div>
              </div>
            </div>
            {/* End Score side */}
            {/* Start Dummy side */}
            <div className="Dummyside">
              <div className="container"></div>
            </div>
          </div>
          {/* End Dummy side */}
        </main>
      </MainLayout>
    </>
  );
};

export default TeamStandings;
